import React, { useState } from 'react';
import { XIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: '1. Información que Recopilamos',
    body: 'Recopilamos información personal que nos proporcionas directamente, como tu nombre, dirección de correo electrónico y cualquier otra información que decidas compartir al utilizar nuestros servicios o comunicarte con nosotros.'
  }, {
    heading: '2. Uso de la Información',
    body: 'Utilizamos la información recopilada para proporcionar, mantener y mejorar nuestros servicios, enviarte comunicaciones relacionadas con tu cuenta o transacciones, y responder a tus consultas y solicitudes.'
  }, {
    heading: '3. Protección de Datos',
    body: 'Implementamos medidas de seguridad técnicas y organizativas apropiadas para proteger tu información personal contra el acceso no autorizado, la alteración, divulgación o destrucción.'
  }, {
    heading: '4. Cookies y Tecnologías Similares',
    body: 'Utilizamos cookies y tecnologías similares para mejorar tu experiencia de navegación, analizar el uso del sitio y personalizar el contenido. Puedes configurar tu navegador para rechazar cookies.'
  }, {
    heading: '5. Tus Derechos',
    body: 'Tienes derecho a acceder, rectificar, eliminar y portar tus datos personales. También puedes oponerte al procesamiento de tus datos o solicitar la limitación del mismo contactándonos directamente.'
  }, {
    heading: '6. Cambios en esta Política',
    body: 'Nos reservamos el derecho de actualizar esta política de privacidad en cualquier momento. Te notificaremos sobre cambios significativos publicando la nueva política en nuestro sitio web.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: '1. Aceptación de los Términos',
    body: 'Al acceder y utilizar este sitio web y nuestros servicios, aceptas estar sujeto a estos términos y condiciones. Si no estás de acuerdo con alguna parte de estos términos, no debes utilizar nuestros servicios.'
  }, {
    heading: '2. Descripción del Servicio',
    body: 'El Sistema de Triple Filtro es un programa educativo y de consultoría. Los resultados pueden variar según el esfuerzo, dedicación y circunstancias individuales de cada participante.'
  }, {
    heading: '3. Propiedad Intelectual',
    body: 'Todo el contenido, materiales, metodologías y recursos proporcionados son propiedad exclusiva de El Sistema de Triple Filtro. Queda prohibida su reproducción, distribución o uso comercial sin autorización expresa.'
  }, {
    heading: '4. Política de Reembolso',
    body: 'Las solicitudes de reembolso se evaluarán caso por caso según las condiciones establecidas en el momento de la compra. Consulta las condiciones específicas de tu programa para más detalles.'
  }, {
    heading: '5. Limitación de Responsabilidad',
    body: 'No garantizamos resultados específicos. Nuestros servicios son de naturaleza educativa y los resultados dependen de múltiples factores individuales. No nos hacemos responsables de decisiones tomadas basándose en nuestro contenido.'
  }, {
    heading: '6. Ley Aplicable',
    body: 'Estos términos se rigen por las leyes aplicables en la jurisdicción correspondiente. Cualquier disputa será resuelta mediante los tribunales competentes de dicha jurisdicción.'
  }]
};
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const content = activeModal === 'privacy' ? privacyContent : termsContent;
  return <>
      <footer className="bg-brand-charcoal py-8 border-t border-white/10 text-center text-brand-cream/50 text-sm" data-id="element-62">
        <p className="mb-3" data-id="element-63">
          © {new Date().getFullYear()} El Sistema de Triple Filtro. Todos los
          derechos reservados.
        </p>
        <div className="flex items-center justify-center gap-4" data-id="element-64">
          <button onClick={() => setActiveModal('privacy')} className="hover:text-brand-cream transition-colors duration-200 underline underline-offset-2" data-id="element-65">
            Política de Privacidad
          </button>
          <span className="text-brand-cream/20" data-id="element-66">|</span>
          <button onClick={() => setActiveModal('terms')} className="hover:text-brand-cream transition-colors duration-200 underline underline-offset-2" data-id="element-67">
            Términos y Condiciones
          </button>
        </div>
      </footer>

      <AnimatePresence data-id="element-68">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-69">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-70">
              <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-71">
                <h2 className="text-xl font-bold text-brand-charcoal" data-id="element-72">
                  {content.title}
                </h2>
                <button onClick={() => setActiveModal(null)} className="p-2 rounded-full hover:bg-gray-100 transition-colors duration-200 text-gray-400 hover:text-gray-600" aria-label="Cerrar" data-id="element-73">
                  <XIcon className="w-5 h-5" data-id="element-74" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)] space-y-6" data-id="element-75">
                {content.sections.map((section, i) => <div key={i} data-id="element-76">
                    <h3 className="font-semibold text-brand-charcoal mb-2" data-id="element-77">
                      {section.heading}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-sm" data-id="element-78">
                      {section.body}
                    </p>
                  </div>)}

                <p className="text-xs text-gray-400 pt-4 border-t border-gray-100" data-id="element-79">
                  Última actualización:{' '}
                  {new Date().toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
                </p>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}